import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './pages.css';
import config, { setRunnerUrl } from '../Config';
import { setTokenCookie } from '../functions/authSession';
import { cognitoLogin, cognitoResendCode, cognitoConfirmSignup } from '../functions/cognitoAuth';
import { dedupedFetch } from '../functions/dedupedFetch';
import { pickRunnerUrlFromUserInfo } from '../functions/cognitoIdToken';
import { persistUserIdFromClusters } from '../functions/clusterSwitch';
import { clearRestrictedSession, isRestrictedResponse } from '../functions/restrictedAccess';
import FormControl from '@mui/material/FormControl';
import Loader from './Loader';

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [unverified, setUnverified] = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      setErrorDetail('Please enter email and password.');
      setShowErrorPopup(true);
      return;
    }
    setLoading(true);
    try {
      const tokens = await cognitoLogin(email, password);

      setTokenCookie(tokens.idToken);

      let restricted = false;
      try {
        const res = await dedupedFetch(`${config.AUTH_API}/v1/get-user-info`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${tokens.idToken}`,
          },
        });
        restricted = await isRestrictedResponse(res);
        if (res.ok) {
          const info = await res.json();
          const url = pickRunnerUrlFromUserInfo(info, tokens.idToken);
          if (url) setRunnerUrl(url);
          persistUserIdFromClusters(info);
        }
      } catch { /* non-critical */ }

      // Credentials were valid but this environment does not serve the account, so
      // drop the session instead of letting it reach a dashboard that only 403s.
      if (restricted) {
        clearRestrictedSession(email);
        navigate('/not-authorized');
        return;
      }

      setShowLoginPopup(true);
    } catch (err) {
      if (err.code === 'UserNotConfirmedException') {
        setUnverified(true);
      } else {
        setErrorDetail(err.message || 'Login failed. Please try again.');
        setShowErrorPopup(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  const handleResendCode = async () => {
    setResendStatus('');
    try {
      await cognitoResendCode(email);
      setResendStatus('Verification email sent. Please check your inbox.');
      setCodeSent(true);
    } catch (err) {
      setResendStatus(err.message || 'Failed to resend. Please try again.');
    }
  };

  const handleConfirmCode = async () => {
    if (!verifyCode) { setResendStatus('Please enter the verification code.'); return; }
    try {
      await cognitoConfirmSignup(email, verifyCode);
      const tokens = await cognitoLogin(email, password);
      setTokenCookie(tokens.idToken);
      let restricted = false;
      try {
        const res = await dedupedFetch(`${config.AUTH_API}/v1/signup`, {
          method: 'POST',
          headers: { Accept: 'application/json', Authorization: `Bearer ${tokens.idToken}` },
        });
        restricted = await isRestrictedResponse(res);
      } catch { /* non-critical */ }
      try {
        const res = await dedupedFetch(`${config.AUTH_API}/v1/get-user-info`, {
          method: 'GET',
          headers: { Accept: 'application/json', Authorization: `Bearer ${tokens.idToken}` },
        });
        restricted = restricted || await isRestrictedResponse(res);
        if (res.ok) {
          const info = await res.json();
          const url = pickRunnerUrlFromUserInfo(info, tokens.idToken);
          if (url) setRunnerUrl(url);
          persistUserIdFromClusters(info);
        }
      } catch { /* non-critical */ }
      if (restricted) {
        clearRestrictedSession(email);
        navigate('/not-authorized');
        return;
      }
      setUnverified(false);
      setCodeSent(false);
      setVerifyCode('');
      setResendStatus('');
      navigate('/');
    } catch (err) {
      setResendStatus(err.message || 'Invalid code. Please try again.');
    }
  };

  return (
    <div className="signin-main">
      <Loader show={loading} />
      <div className="auth-left">
        <div className="auth-left-top">
          <Link to="/" className="auth-left-logo">Mermadic</Link>
          <span className="auth-left-badge">Managed cloud infrastructure</span>
        </div>
        <div className="auth-left-body">
          <h1 className="auth-left-headline">
            Dedicated clusters.<br />Private VPCs.<br />Fully managed.
          </h1>
          <p className="auth-left-desc">
            Run jobs, deploy services, and automate pipelines on dedicated infrastructure — provisioned in minutes, managed entirely by Mermadic.
          </p>
          <ul className="auth-left-bullets">
            <li>Isolated cluster and private VPC per account</li>
            <li>Jobs, deployments, and CDCI in one platform</li>
            <li>No cloud account or DevOps experience needed</li>
          </ul>
        </div>
      </div>
      <div className="login-container">
        <div className="login-card">
          <Link to="/" className="auth-compact-brand">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to home
          </Link>
          <h2 className="signin-header">Welcome back</h2>
          <p className="signin-sub">Log in to your Mermadic account</p>

          <FormControl variant="standard" className="cnt-form">
            <div className="login-field">
              <label htmlFor="email">Email</label>
              <input
                type="email" id="email" placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div className="login-field">
              <div className="login-field-top">
                <label htmlFor="password">Password</label>
                <Link className="login-forgot" to="/forgot-password">Forgot password?</Link>
              </div>
              <input
                type="password" id="password" placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <button type="button" className="login-submit-btn" onClick={handleLogin} disabled={loading}>
              Log in
            </button>

            <p className="login-signup-hint">
              Don't have an account? <Link to="/signup">Sign up</Link>
            </p>
          </FormControl>
        </div>

        {unverified && (
          <div className="popup">
            <div className="popup-inner" style={{ textAlign: 'center', padding: '36px 32px', maxWidth: '380px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#f0f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <span style={{ fontSize: '1.2rem' }}>✉</span>
              </div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '0 0 6px' }}>Verify your email</h2>
              <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 20px', lineHeight: '1.5' }}>
                {codeSent ? <>Code sent to <strong style={{ color: '#555' }}>{email}</strong></> : "Your account hasn't been verified yet."}
              </p>
              {resendStatus && (
                <p style={{ fontSize: '0.82rem', color: resendStatus.includes('sent') ? '#2e7d32' : '#c62828', margin: '-8px 0 14px', padding: '8px 12px', background: resendStatus.includes('sent') ? '#f0faf0' : '#fff5f5', borderRadius: '6px' }}>
                  {resendStatus}
                </p>
              )}
              {codeSent && (
                <>
                  <input
                    type="text"
                    placeholder="· · · · · ·"
                    maxLength={6}
                    value={verifyCode}
                    onChange={e => setVerifyCode(e.target.value)}
                    style={{ width: '100%', padding: '11px', marginBottom: '12px', borderRadius: '8px', border: '1.5px solid #e0e0e0', textAlign: 'center', fontSize: '1.3rem', letterSpacing: '8px', boxSizing: 'border-box', outline: 'none', fontWeight: '600' }}
                  />
                  <button onClick={handleConfirmCode} style={{ width: '100%', padding: '11px', marginBottom: '10px', borderRadius: '8px', background: '#2563eb', color: '#fff', border: 'none', fontSize: '0.9rem', fontWeight: '400', cursor: 'pointer' }}>
                    Verify &amp; log in
                  </button>
                </>
              )}
              {!codeSent && (
                <button onClick={handleResendCode} style={{ width: '100%', padding: '11px', marginBottom: '10px', borderRadius: '8px', background: '#2563eb', color: '#fff', border: 'none', fontSize: '0.9rem', fontWeight: '400', cursor: 'pointer' }}>
                  Send verification email
                </button>
              )}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '4px' }}>
                {codeSent && (
                  <button onClick={handleResendCode} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.83rem', padding: '0' }}>
                    Resend code
                  </button>
                )}
                <button style={{ background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: '0.83rem', padding: '0' }} onClick={() => { setUnverified(false); setResendStatus(''); setCodeSent(false); setVerifyCode(''); }}>
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}
        {showErrorPopup && (
          <div className="popup">
            <div className="popup-inner popup-error">
              <span className="popup-icon">✕</span>
              <h2>Login failed</h2>
              <div>{errorDetail}</div>
              <button onClick={() => { setShowErrorPopup(false); setErrorDetail(''); }}>Try again</button>
            </div>
          </div>
        )}
        {showLoginPopup && (
          <div className="popup">
            <div className="popup-inner popup-success">
              <span className="popup-icon">✓</span>
              <h2>Welcome back!</h2>
              <div>You've logged in successfully.</div>
              <button onClick={() => { setShowLoginPopup(false); navigate('/'); }}>Go to dashboard</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
